import { Search, X, ChevronDown, CheckSquare, Square } from "lucide-react";
import { VisibleColumns } from "./ColumnToggle";

interface InventoryFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  selectedCategory: string;
  onCategoryChange: (value: string) => void;
  categories: string[];
  stockFilter: string;
  onStockFilterChange: (value: string) => void;
  visibleColumns: VisibleColumns;
  onVisibleColumnsChange: (columns: VisibleColumns) => void;
  isMobile?: boolean;
  selectionMode?: boolean;
  onToggleSelectionMode?: () => void;
}

export function InventoryFilters({
  searchTerm,
  onSearchChange,
  selectedCategory,
  onCategoryChange,
  categories,
  stockFilter,
  onStockFilterChange,
  isMobile = false,
  selectionMode = false,
  onToggleSelectionMode,
}: InventoryFiltersProps) {
  const activeFiltersCount = [
    selectedCategory !== "" ? selectedCategory : null,
    stockFilter !== "" ? stockFilter : null,
  ].filter((f) => f !== null).length;

  const clearAllFilters = () => {
    onCategoryChange("");
    onStockFilterChange("");
  };

  return (
    <div className="bg-[var(--card)] rounded-xl shadow-card border border-[var(--border)] p-3 md:p-4">
      <div className="space-y-3">
        {/* Search Bar with Selection Mode Toggle */}
        <div className="flex gap-2">
          {/* Search Bar */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-[var(--muted-foreground)] pointer-events-none" />
            <input
              id="inventory-search"
              type="text"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Rechercher un article..."
              className="w-full pl-10 md:pl-11 pr-10 py-2 md:py-2.5 text-sm md:text-base border border-[var(--switch-background)] bg-[var(--muted)] text-[var(--foreground)] placeholder-[var(--muted-foreground-light)] rounded-lg hover:border-[var(--color-steel-darker)] focus:border-[var(--color-steel)] focus:ring-2 focus:ring-[var(--color-steel-bg)] focus:outline-none transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => onSearchChange("")}
                className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-[var(--switch-background)] rounded transition-colors group"
                title="Effacer la recherche"
              >
                <X className="w-4 h-4 text-[var(--muted-foreground-light)] group-hover:text-[var(--foreground)]" />
              </button>
            )}
          </div>

          {/* Selection Mode Toggle Button */}
          {onToggleSelectionMode && (
            <button
              onClick={onToggleSelectionMode}
              className={`px-3 md:px-4 py-2 md:py-2.5 rounded-lg transition-all border flex items-center gap-2 whitespace-nowrap ${
                selectionMode
                  ? "bg-gradient-to-br from-[var(--color-steel)] to-[var(--color-steel-dark)] text-white border-[var(--color-steel)] shadow-lg shadow-[var(--color-steel)]/20"
                  : "bg-[var(--muted)] hover:bg-[var(--border)] text-[var(--muted-foreground)] hover:text-[var(--foreground)] border-[var(--switch-background)]"
              }`}
              title={selectionMode ? "Désactiver la sélection" : "Activer la sélection multiple"}
            >
              {selectionMode ? (
                <CheckSquare className="w-4 h-4 md:w-5 md:h-5" />
              ) : (
                <Square className="w-4 h-4 md:w-5 md:h-5" />
              )}
              <span className="hidden sm:inline">
                {selectionMode ? "Sélection" : "Sélectionner"}
              </span>
            </button>
          )}
        </div>

        {/* Direct Filters - Always Visible */}
        <div className="flex flex-col sm:flex-row gap-2">
          {/* Category Filter */}
          <div className="relative flex-1">
            <select
              value={selectedCategory}
              onChange={(e) => onCategoryChange(e.target.value)}
              className="w-full appearance-none pl-3 pr-8 py-2 border border-[var(--switch-background)] bg-[var(--muted)] text-[var(--foreground)] rounded-lg focus:ring-2 focus:ring-[var(--color-steel)] focus:border-transparent transition-all text-sm cursor-pointer hover:bg-[var(--border)]"
            >
              <option value="">Toutes les catégories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--muted-foreground)] pointer-events-none" />
          </div>

          {/* Stock Filter */}
          <div className="relative flex-1">
            <select
              value={stockFilter}
              onChange={(e) => onStockFilterChange(e.target.value)}
              className="w-full appearance-none pl-3 pr-8 py-2 border border-[var(--switch-background)] bg-[var(--muted)] text-[var(--foreground)] rounded-lg focus:ring-2 focus:ring-[var(--color-steel)] focus:border-transparent transition-all text-sm cursor-pointer hover:bg-[var(--border)]"
            >
              <option value="">Tous les niveaux</option>
              <option value="rupture">Rupture (0)</option>
              <option value="bas">Stock bas (≤10)</option>
              <option value="ok">Stock ok (&gt;10)</option>
              <option value="commander">À commander</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--muted-foreground)] pointer-events-none" />
          </div>

          {/* Clear Filters Button */}
          {activeFiltersCount > 0 && (
            <button
              onClick={clearAllFilters}
              className="px-3 py-2 bg-[var(--color-brick)]/10 hover:bg-[var(--color-brick)]/20 text-[var(--color-brick)] border border-[var(--color-brick)]/30 rounded-lg transition-all text-sm font-medium whitespace-nowrap flex items-center gap-1.5"
              title="Effacer tous les filtres"
            >
              <X className="w-4 h-4" />
              <span className="hidden sm:inline">Effacer</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}